import { useEffect } from 'react';
import type { MapRef } from 'react-map-gl/mapbox';
import { basemapTrailheadsLayerId } from '../utils/layers';

/**
 * Hides the basemap style's own trailhead layer while the trailheads overlay draws its pins, so the two don't
 * render on top of each other. Restores the layer when the overlay unmounts.
 *
 * Mapbox throws from getStyle, getLayer and setLayoutProperty while a style is loading or being swapped (for example
 * switching base styles, or the map being torn down as the overlay unmounts), so every call into the map is guarded.
 */
export default function useHideBasemapTrailheadLayer(map: MapRef | undefined) {
  useEffect(() => {
    // MapRef leaves out setLayoutProperty, so go through the underlying Mapbox map
    const mapboxMap = map?.getMap();
    if (!mapboxMap) {
      return;
    }

    // Whether this hook is the one that hid the layer, so cleanup doesn't show a layer the style hides itself
    let didHideLayer = false;

    const hideLayer = () => {
      try {
        // No style yet (or still loading): the style.load listener below runs this again
        if (!mapboxMap.getStyle() || !mapboxMap.getLayer(basemapTrailheadsLayerId)) {
          didHideLayer = false;
          return;
        }
        didHideLayer = mapboxMap.getLayoutProperty(basemapTrailheadsLayerId, 'visibility') !== 'none';
        if (didHideLayer) {
          mapboxMap.setLayoutProperty(basemapTrailheadsLayerId, 'visibility', 'none');
        }
      } catch {
        // The style isn't ready to change. The layer stays as it was, and the next style.load tries again.
        didHideLayer = false;
      }
    };

    hideLayer();
    // A new base style (for example switching to satellite) replaces the layer, so hide it again
    mapboxMap.on('style.load', hideLayer);

    return () => {
      try {
        mapboxMap.off('style.load', hideLayer);
        if (didHideLayer && mapboxMap.getStyle() && mapboxMap.getLayer(basemapTrailheadsLayerId)) {
          mapboxMap.setLayoutProperty(basemapTrailheadsLayerId, 'visibility', 'visible');
        }
      } catch {
        // The map or its style is already gone or mid-swap, and a new style brings the layer back with its default visibility
      }
    };
  }, [map]);
}
